import classNames from "classnames";
import { isEmpty } from "lodash";
import * as React from "react";
import { RefObject } from "react";
import Annotation, {
    AnnotationItem,
    RenderContentParams,
    RenderEditorParams,
    RenderHighlightParams,
    RenderSelectorParams,
} from "~/sharedComponents/imageAnnotation/components/Annotation";
import { ClickOutside, CloseCallback, CloseReason } from "~/sharedHOC/ClickOutside/ClickOutside";
import {
    clearAnnotation,
    createAnnotation,
    deleteAnnotation,
    enterEditMode,
    enterViewMode,
    hideMessages,
    nextImage,
    previousImage,
    setAnnotation,
    updateAnnotation,
} from "../actions";
import { AnnotationItemExtended, Image } from "../types";
import * as styles from "./ImageAnnotations.less";
import { ImageAnnotationsComments } from "./ImageAnnotationsComments";
import { AnnotationEditor } from "./ImageAnnotationsEditor";
import { ImageAnnotationView } from "./ImageAnnotationView";
import { Point } from "./Point";

export interface ImageAnnotationsProps {
    annotations?: AnnotationItemExtended[];
    annotation?: AnnotationItemExtended;
    hasAnnotations?: boolean;
    activeAnnotations?: AnnotationItemExtended[];
    setAnnotation?: typeof setAnnotation;
    clearAnnotation?: typeof clearAnnotation;
    createAnnotation?: typeof createAnnotation;
    updateAnnotation?: typeof updateAnnotation;
    deleteAnnotation?: typeof deleteAnnotation;
    isMessagesShown?: boolean;
    hideMessages?: typeof hideMessages;
    enterEditMode?: typeof enterEditMode;
    enterViewMode?: typeof enterViewMode;
    previousImage: typeof previousImage;
    nextImage: typeof nextImage;
    attachmentId: number;
    disabledNavigation: boolean;
    image: Image;
}

interface State {
    showEditor: boolean;
}

export enum ImageAnnotationDotTypes {
    point = "POINT",
}

export enum ImageAnnotationKeyTypes {
    content = "CONTENT",
    message = "MESSAGE",
    selector = "SELECTOR",
}

export class ImageAnnotations extends React.Component<ImageAnnotationsProps, State> {
    annotationRef: RefObject<HTMLDivElement> = React.createRef();
    cancelFunc: (props: RenderEditorParams) => () => void;
    constructor(props: ImageAnnotationsProps) {
        super(props);
        this.state = {
            showEditor: false,
        };
        this.onChange = this.onChange.bind(this);
        this.onSubmit = this.onSubmit.bind(this);
        this.renderSelector = this.renderSelector.bind(this);
        this.renderHighlight = this.renderHighlight.bind(this);
        this.renderEditor = this.renderEditor.bind(this);
        this.renderContent = this.renderContent.bind(this);
        this.hideMessagesContainer = this.hideMessagesContainer.bind(this);
        this.onOpen = this.onOpen.bind(this);
        this.onMessageChange = this.onMessageChange.bind(this);
        this.onAnnotationClick = this.onAnnotationClick.bind(this);
        this.handleClickOutside = this.handleClickOutside.bind(this);
        this.cancelFunc = (props: RenderEditorParams) => () => {
            if (this.props.annotation.data && this.props.annotation.data.id) {
                this.props.enterViewMode(props.annotation);
            } else {
                this.props.clearAnnotation();
            }
        };
    }

    onChange(annotation: AnnotationItemExtended) {
        const { data, message } = this.props.annotation;
        if (data && !message) {
            this.props.setAnnotation({
                ...this.props.annotation,
                geometry: annotation.geometry,
            });
        } else {
            this.props.setAnnotation(annotation);
        }
    }

    onMessageChange(annotation: AnnotationItemExtended) {
        this.props.setAnnotation(annotation);
    }

    onOpen(annotation: AnnotationItemExtended) {
        this.props.enterViewMode(annotation);
    }

    onSubmit() {
        const { geometry, data } = this.props.annotation;

        if (!data || !data.text) {
            return false;
        }

        if (this.props.annotation.message) {
            this.props.updateAnnotation(this.props.attachmentId, this.props.annotation);
            this.props.enterViewMode(this.props.annotation);
        } else {
            const fakeAnnotation = {
                geometry,
                data: {
                    ...data,
                    id: Math.random(),
                },
                message: {
                    userId: ep.settings.currentUserId,
                    userName: ep.settings.currentUserName,
                },
            };
            this.props.createAnnotation(this.props.attachmentId, fakeAnnotation);
            this.props.enterViewMode(fakeAnnotation);
        }
    }
    onAnnotationClick(annotation: AnnotationItem) {
        this.props.enterViewMode(annotation);
    }

    activeAnnotationComparator(annotation: AnnotationItem, active: AnnotationItem) {
        return annotation.data.id === active.data.id;
    }

    renderSelector(props: RenderSelectorParams) {
        if (isEmpty(props.annotation.geometry)) {
            return null;
        }
        return <Point geometry={props.annotation.geometry} active={true} />;
    }

    renderHighlight(props: RenderHighlightParams) {
        if (isEmpty(props.annotation.geometry)) {
            return null;
        }
        const active = props.annotation.data.hover || props.active;
        return (
            <Point
                geometry={props.annotation.geometry}
                active={active}
                key={ImageAnnotationKeyTypes.selector + props.annotation.data.id}
            />
        );
    }

    renderEditor(props: RenderEditorParams) {
        if (isEmpty(props.annotation.geometry)) {
            return null;
        }

        return (
            <ClickOutside containerElRef={this.annotationRef} closeActionHandler={this.handleClickOutside}>
                <AnnotationEditor
                    isNew={!this.props.annotation.message}
                    annotation={props.annotation}
                    onChange={this.onMessageChange}
                    onSubmit={this.onSubmit}
                    onCancel={this.cancelFunc(props)}
                    withScroll={true}
                />
            </ClickOutside>
        );
    }

    renderContent(props: RenderContentParams) {
        const startEditFunc = (value: AnnotationItemExtended) => {
            this.props.enterEditMode(value);
        };

        return (
            <ImageAnnotationView
                annotation={props.annotation}
                startEdit={startEditFunc}
                handleClickOutside={this.handleClickOutside}
                annotationContainerRef={this.annotationRef}
                onChange={this.onChange}
                onSubmit={this.onSubmit}
                key={ImageAnnotationKeyTypes.content + this.props.annotation.data.id}
            />
        );
    }

    hideMessagesContainer() {
        this.props.hideMessages();
    }

    handleClickOutside: CloseCallback = reason => {
        if (this.props.annotation && reason === CloseReason.CLICK_OUTSIDE) {
            this.props.clearAnnotation();
        }
    };

    render() {
        const {
            image: { source, filename },
            annotation,
            annotations,
            previousImage,
            nextImage,
            disabledNavigation,
            isMessagesShown,
            hasAnnotations,
        } = this.props;
        const previousClasses = classNames(styles.previous, disabledNavigation && styles.disabled);
        const nextClasses = classNames(styles.next, disabledNavigation && styles.disabled);

        return (
            <>
                <div className={classNames(styles.wrapper, isMessagesShown && styles.with_messages)}>
                    <div className={previousClasses} onClick={previousImage} />
                    <div ref={this.annotationRef}>
                        <Annotation
                            key={source}
                            src={source}
                            alt={filename}
                            annotations={annotations}
                            activeAnnotationComparator={this.activeAnnotationComparator}
                            disableOverlay={true}
                            value={annotation}
                            type={ImageAnnotationDotTypes.point}
                            renderSelector={this.renderSelector}
                            renderHighlight={this.renderHighlight}
                            renderContent={this.renderContent}
                            onChange={this.onChange}
                            onSubmit={this.onSubmit}
                            onAnnotationClick={this.onAnnotationClick}
                            onOpen={this.onOpen}
                            renderEditor={this.renderEditor}
                        />
                    </div>
                    <div className={nextClasses} onClick={nextImage} />
                </div>
                {isMessagesShown && (
                    <ImageAnnotationsComments
                        annotations={annotations}
                        hideMessagesContainer={this.hideMessagesContainer}
                        isMessagesShown={isMessagesShown}
                        hasAnnotations={hasAnnotations}
                        onChange={this.onChange}
                        onSubmit={this.onSubmit}
                    />
                )}
            </>
        );
    }
}
